/**
 * TanStack Query hooks for the admin catalog-suggestions queue — PIM Wave
 * P3 Task 4. Mirrors the sibling `product-review/hooks.ts` idiom (own
 * api.ts, own hooks.ts, prefix-invalidate helper, 30s refetch / 10s stale,
 * oldest-first, limit 50).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  approveSuggestion,
  declineSuggestion,
  getSuggestionCounts,
  listSuggestions,
  suggestionKeys,
  type SuggestionApproveRequest,
  type SuggestionApproveResponse,
  type SuggestionCounts,
  type SuggestionListResponse,
  type SuggestionResponse,
} from "@/features/admin/suggestions/api";

const REFETCH_INTERVAL = 30_000;
const PAGE_LIMIT = 50;

export function useSuggestions(
  status: string,
): UseInfiniteQueryResult<InfiniteData<SuggestionListResponse>, Error> {
  return useInfiniteQuery<
    SuggestionListResponse,
    Error,
    InfiniteData<SuggestionListResponse>,
    ReturnType<typeof suggestionKeys.list>,
    string | null
  >({
    queryKey: suggestionKeys.list(status),
    queryFn: ({ pageParam }) =>
      listSuggestions({ status, cursor: pageParam, limit: PAGE_LIMIT }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: 10_000,
  });
}

export function useSuggestionCounts(): UseQueryResult<SuggestionCounts, Error> {
  return useQuery<SuggestionCounts, Error>({
    queryKey: suggestionKeys.counts(),
    queryFn: () => getSuggestionCounts(),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: 10_000,
  });
}

function useInvalidateAfterSuggestionWrite(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["admin", "suggestions", "list"] }),
      qc.invalidateQueries({ queryKey: suggestionKeys.counts() }),
    ]);
  };
}

export interface ApproveSuggestionVariables {
  id: string;
  body: SuggestionApproveRequest;
}

export function useApproveSuggestion(): UseMutationResult<
  SuggestionApproveResponse,
  Error,
  ApproveSuggestionVariables
> {
  const invalidate = useInvalidateAfterSuggestionWrite();
  return useMutation<SuggestionApproveResponse, Error, ApproveSuggestionVariables>({
    mutationFn: ({ id, body }) => approveSuggestion(id, body),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export interface DeclineSuggestionVariables {
  id: string;
  reason: string;
}

export function useDeclineSuggestion(): UseMutationResult<
  SuggestionResponse,
  Error,
  DeclineSuggestionVariables
> {
  const invalidate = useInvalidateAfterSuggestionWrite();
  return useMutation<SuggestionResponse, Error, DeclineSuggestionVariables>({
    mutationFn: ({ id, reason }) => declineSuggestion(id, reason),
    onSuccess: () => {
      void invalidate();
    },
  });
}
